import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, CameraOff, Loader2, Mic, MicOff, PhoneOff, Radio } from "lucide-react";
import { RtkParticipantsAudio } from "@cloudflare/realtimekit-react-ui";
import VideoTile from "@/components/studio/VideoTile";
import { useRtkMeeting, useRoster, useConnectionHealth, useUplinkStats } from "@/hooks/useRtkMeeting";
import ConnectionDot from "@/components/studio/ConnectionDot";
import { useShowSocket } from "@/hooks/useShowSocket";
import { studioApi, errMessage } from "@/lib/studioApi";

/**
 * Guest join — one link, no account.
 *   1. Enter name / title / organization (pre-filled for signed-in members)
 *   2. Camera check while waiting in the lobby
 *   3. Producer admits -> joins the room -> simple in-show view with ON AIR indicator
 */
const storeKey = (token) => `fc_join_${token}`;

export default function GuestJoin() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", title: "", org: "" });
  const [me, setMe] = useState(() => { try { return JSON.parse(sessionStorage.getItem(storeKey(token)) || "null"); } catch (e) { return null; } });
  const [status, setStatus] = useState(me ? "lobby" : "form");
  const [authToken, setAuthToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    studioApi.joinInfo(token).then((r) => {
      setInfo(r);
      if (r.prefill) setForm((f) => ({ name: f.name || r.prefill.name || "", title: f.title || r.prefill.title || "", org: f.org || r.prefill.org || "" }));
    }).catch((e) => setError(errMessage(e, "This link isn't valid")));
  }, [token]);

  // Poll until admitted (cheap, 3s) — guests don't need a WS before they're in.
  useEffect(() => {
    if (!me || status !== "lobby") return undefined;
    let stop = false;
    const poll = async () => {
      try {
        const r = await studioApi.joinStatus(token, me.participant_id, me.join_key);
        if (stop) return;
        if (r.show_status === "ended") { setStatus("ended"); return; }
        if (r.status === "admitted" && r.auth_token) { setAuthToken(r.auth_token); setStatus("admitted"); return; }
        if (r.status === "declined") { setStatus("declined"); return; }
        if (r.status === "removed") { setStatus("removed"); return; }
      } catch (e) { /* keep polling */ }
      if (!stop) setTimeout(poll, 3000);
    };
    poll();
    return () => { stop = true; };
  }, [me, status, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const r = await studioApi.joinRequest(token, form);
      const rec = { participant_id: r.participant_id, join_key: r.join_key, name: form.name };
      sessionStorage.setItem(storeKey(token), JSON.stringify(rec));
      setMe(rec);
      setStatus("lobby");
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <Shell title="Hmm."><p className="text-sm text-zinc-300">{error}</p></Shell>;
  if (!info) return <Shell><Loader2 className="h-6 w-6 animate-spin text-[#e6a25f]" /></Shell>;

  if (status === "admitted" && authToken) {
    return <InShow authToken={authToken} me={me} show={info.show} token={token} onLeft={() => { sessionStorage.removeItem(storeKey(token)); setMe(null); setStatus("left"); }} />;
  }

  return (
    <Shell title={info.show.title} subtitle={status === "form" ? "You've been invited to join as a guest." : null}>
      {status === "form" && (
        <form onSubmit={submit} className="space-y-3" data-testid="guest-form">
          <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="guest-name" />
          <Field label="Title (shown on your name plate)" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Founder, Director, …" testId="guest-title" />
          <Field label="Organization" value={form.org} onChange={(v) => setForm({ ...form, org: v })} placeholder="Company or nonprofit" testId="guest-org" />
          <button type="submit" disabled={submitting || !form.name.trim()} className="mt-2 w-full rounded-lg bg-[#c9772e] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#d98a3f] disabled:opacity-50" data-testid="guest-submit">
            {submitting ? "Joining…" : "Continue to the lobby"}
          </button>
          <p className="text-center text-xs text-zinc-500">No account needed. The producer will let you in.</p>
        </form>
      )}
      {status === "lobby" && <Lobby me={me} />}
      {status === "declined" && <p className="text-sm text-zinc-300" data-testid="guest-declined">The producer isn't able to bring you in right now. Thanks for being here!</p>}
      {status === "removed" && <p className="text-sm text-zinc-300">You've left the show. Thanks for joining us!</p>}
      {status === "left" && <p className="text-sm text-zinc-300" data-testid="guest-left">You've left the show. Thanks for joining us!</p>}
      {status === "ended" && <p className="text-sm text-zinc-300">This show has ended. Thanks for being here!</p>}
    </Shell>
  );
}

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0a0f17] px-4 py-10 text-zinc-100" data-testid="guest-join">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <img src="/logo-mark.png" alt="" className="h-11 w-11" />
          <div className="leading-tight"><div className="text-sm font-bold tracking-[0.2em]">FRIDAY</div><div className="text-[10px] font-semibold tracking-[0.35em] text-[#e6a25f]">CONVERSATIONS</div></div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#0f1520] p-6 shadow-2xl">
          {title && <h1 className="text-lg font-semibold" data-testid="guest-show-title">{title}</h1>}
          {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, testId }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-zinc-400">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className="mt-1 w-full rounded-lg border border-white/10 bg-[#0a0f17] px-3 py-2.5 text-sm outline-none focus:border-[#c9772e]" data-testid={testId} />
    </label>
  );
}

/** Local camera check while waiting. Uses getUserMedia directly (no room yet). */
function Lobby({ me }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camError, setCamError] = useState(null);
  useEffect(() => {
    let stopped = false;
    navigator.mediaDevices?.getUserMedia({ video: { width: 1280, height: 720 }, audio: true }).then((s) => {
      if (stopped) { s.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
    }).catch(() => setCamError("We couldn't access your camera or microphone. Check your browser permissions."));
    return () => { stopped = true; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);
  return (
    <div data-testid="guest-lobby">
      <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
        <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
        {camError && <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-zinc-300">{camError}</div>}
      </div>
      <div className="mt-4 flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-200">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> Hi {me?.name?.split(" ")[0]} — you're in the lobby. The producer will bring you in shortly.
      </div>
      <p className="mt-3 text-xs text-zinc-500">Keep this tab open. Find a quiet spot, face a light source, and you're set.</p>
    </div>
  );
}

function InShow({ authToken, me, show, token, onLeft }) {
  const { meeting, joined, error, leave } = useRtkMeeting(authToken, { audio: true, video: true });
  const roster = useRoster(meeting);
  const health = useConnectionHealth(meeting);
  const uplink = useUplinkStats(meeting, joined);
  const [notice, setNotice] = useState(null);
  const meetingRef = useRef(null);
  meetingRef.current = meeting;
  // Producer's audio mixer: mute/unmute requests arrive over the show socket; we comply and tell the guest.
  const onShowEvent = useCallback((msg) => {
    if (msg.type !== "mic_control" || !(msg.participant_ids || []).includes(me.participant_id)) return;
    const self = meetingRef.current?.self;
    if (!self) return;
    try { if (msg.enabled) self.enableAudio(); else self.disableAudio(); } catch (e) { /* noop */ }
    setNotice(msg.enabled ? `${msg.by || "The producer"} turned your mic on` : `${msg.by || "The producer"} muted your mic`);
    setTimeout(() => setNotice(null), 4000);
  }, [me.participant_id]);
  const { show: liveShow } = useShowSocket(show.show_id, { participantId: me.participant_id, joinKey: me.join_key }, undefined, onShowEvent);
  const program = liveShow?.program;
  const onAir = useMemo(() => {
    if (!program) return false;
    if (program.layout === "speaker") return true;
    return (program.sources || []).includes(me.participant_id);
  }, [program, me.participant_id]);
  const [, force] = useState(0);
  const self = meeting?.self;
  const others = roster.list.filter((e) => !e.isSelf);
  const doLeave = useCallback(async () => { await leave(); onLeft(); }, [leave, onLeft]);
  const ended = liveShow?.status === "ended";
  useEffect(() => { if (ended) doLeave(); }, [ended, doLeave]);

  return (
    <div className="flex min-h-screen flex-col bg-[#0a0f17] text-zinc-100" data-testid="guest-in-show">
      {meeting && <RtkParticipantsAudio meeting={meeting} />}
      <header className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
        <img src="/logo-mark.png" alt="" className="h-8 w-8" />
        <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{show.title}</div><div className="text-[11px] text-zinc-500">You're in the studio as {me.name}</div></div>
        {onAir ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" data-testid="on-air-badge"><Radio className="h-3.5 w-3.5" /> On air</span>
        ) : (
          <span className="rounded-full border border-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-zinc-400" data-testid="off-air-badge">Off air</span>
        )}
      </header>
      <main className="flex-1 p-4">
        {error && <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>}
        {notice && <div className="mb-3 rounded-lg border border-[#c9772e]/40 bg-[#c9772e]/15 px-3 py-2 text-sm text-[#f3c9a0]" data-testid="guest-mic-notice">{notice}</div>}
        <div className={`relative overflow-hidden rounded-2xl border-2 ${onAir ? "border-red-500" : "border-white/10"}`}>
          <VideoTile rtk={self && joined ? self : null} name={me.name} className="aspect-video w-full" />
          {joined && meeting?.self?.id && <ConnectionDot entry={health[meeting.self.id]} uplink={uplink} direction="out" size="badge" className="absolute left-2 top-2" testId="guest-connection" />}
          {!joined && <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-sm"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting…</div>}
        </div>
        {others.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {others.map((e) => (
              <div key={e.peerId} className="overflow-hidden rounded-lg border border-white/10">
                <VideoTile rtk={e.rtk} name={e.name} showBadges className="aspect-video w-full" />
                <div className="truncate bg-[#0f1520] px-2 py-1 text-[11px]">{e.name}</div>
              </div>
            ))}
          </div>
        )}
      </main>
      <footer className="flex items-center justify-center gap-3 border-t border-white/10 px-4 py-3">
        <button type="button" onClick={() => { if (!self) return; (self.audioEnabled ? self.disableAudio() : self.enableAudio()); force((x) => x + 1); }} className={`rounded-full p-3 ${self?.audioEnabled ? "bg-white/10" : "bg-red-600"}`} title="Toggle microphone" data-testid="guest-toggle-mic">{self?.audioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}</button>
        <button type="button" onClick={() => { if (!self) return; (self.videoEnabled ? self.disableVideo() : self.enableVideo()); force((x) => x + 1); }} className={`rounded-full p-3 ${self?.videoEnabled ? "bg-white/10" : "bg-red-600"}`} title="Toggle camera" data-testid="guest-toggle-cam">{self?.videoEnabled ? <Camera className="h-5 w-5" /> : <CameraOff className="h-5 w-5" />}</button>
        <button type="button" onClick={doLeave} className="rounded-full bg-red-600/90 p-3 hover:bg-red-500" title="Leave" data-testid="guest-leave"><PhoneOff className="h-5 w-5" /></button>
      </footer>
    </div>
  );
}
